import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getAccount, updateAccount, deleteAccount,
  updateBlocker, markCompleted, getTeamUsers
} from '../api';
import { toast } from 'sonner';
import { ArrowLeft, Clock, RefreshCw, Trash2, Edit2 } from 'lucide-react';
import { getLevelBadge, getStaleBadge } from '../components/account/helpers';
import StatusCard from '../components/account/StatusCard';
import DevicesCard from '../components/account/DevicesCard';
import BlockersCard from '../components/account/BlockersCard';
import NotesCard from '../components/account/NotesCard';
import { AccountInfoCard, ActivityLog } from '../components/account/AccountSidebar';
import { DeviceModal, BlockerModal, NoteModal, EditAccountModal } from '../components/account/AccountModals';

const AccountDetail = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const {
    user, canEditAccounts, canAddDevices, canAddBlockers,
    canResolveTech, canNotifyClient, canVerifyL3, canApprove,
    canReassign
  } = useAuth();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [showBlockerModal, setShowBlockerModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);

  const isManager = user?.role === 'manager';
  const isOwnTeam = user?.team === account?.current_team || isManager;
  const hasEditAccess = isOwnTeam || ['l2_tech', 'l3_tech'].includes(user?.role);
  const isInboundSales = user?.role === 'inbound_sales';

  const fetchAccount = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAccount(accountId);
      setAccount(response.data);
    } catch (error) {
      toast.error('Failed to fetch account');
      navigate('/queue');
    } finally {
      setLoading(false);
    }
  }, [accountId, navigate]);

  const fetchTeamUsers = useCallback(async (teamName) => {
    try {
      const response = await getTeamUsers(teamName);
      setTeamUsers(response.data);
    } catch (error) {
      toast.error('Failed to load team members');
    }
  }, []);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);
  useEffect(() => {
    if (account?.current_team) fetchTeamUsers(account.current_team);
  }, [account?.current_team, fetchTeamUsers]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateAccount(accountId, { status: newStatus });
      toast.success('Status updated');
      fetchAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAssigneeChange = async (userId) => {
    try {
      await updateAccount(accountId, { assigned_to: userId || null });
      toast.success('Assignee updated');
      fetchAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update assignee');
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Complete and close this account? This action cannot be undone.')) return;
    try {
      await markCompleted(accountId);
      toast.success('Account completed and closed');
      fetchAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete account');
    }
  };

  const getCompletionState = () => {
    if (!account) return { disabled: true, reason: 'Loading...' };
    if (account.status === 'Completed') return { disabled: true, reason: 'Already completed' };
    if (account.status === 'Blocked') return { disabled: true, reason: 'Account is blocked — resolve blockers first' };
    const unresolved = account.blockers.filter(b => !['Verified & Cleared', 'Resolved'].includes(b.status));
    if (unresolved.length > 0) return { disabled: true, reason: `${unresolved.length} blocker(s) still unresolved` };
    return { disabled: false, reason: null };
  };

  const handleBlockerAction = async (blockerId, action, rejectionNote = null) => {
    try {
      const data = { action };
      if (rejectionNote) data.rejection_note = rejectionNote;
      await updateBlocker(accountId, blockerId, data);
      toast.success('Blocker updated');
      fetchAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update blocker');
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete account "${account.account_number} - ${account.account_name}"? This action cannot be undone.`)) return;
    try {
      await deleteAccount(accountId);
      toast.success('Account deleted');
      navigate('/queue');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full spinner" />
      </div>
    );
  }

  if (!account) return null;

  const permissions = { canAddDevices, canAddBlockers, canResolveTech, canNotifyClient, canVerifyL3, canApprove, hasEditAccess, isInboundSales };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/queue')} className="p-2 rounded-md hover:bg-surface-highlight text-text-secondary hover:text-text-primary transition-colors" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-heading font-bold text-text-primary font-mono">{account.account_number}</h1>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getLevelBadge(account.account_level)}`}>
              Level {account.account_level}
            </span>
            {account.stale_flag && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${getStaleBadge(account.account_level)}`}>
                <Clock size={12} /> Stale
              </span>
            )}
          </div>
          <p className="text-text-secondary">{account.account_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {isManager && (
            <>
              <button onClick={() => setShowEditAccountModal(true)} className="p-2 rounded-md bg-surface border border-border text-text-secondary hover:text-primary hover:bg-primary/10 transition-all" title="Edit account details" data-testid="edit-account-btn">
                <Edit2 size={20} />
              </button>
              <button onClick={handleDeleteAccount} className="p-2 rounded-md bg-surface border border-border text-text-secondary hover:text-destructive hover:bg-destructive/10 transition-all" title="Delete account" data-testid="delete-account-btn">
                <Trash2 size={20} />
              </button>
            </>
          )}
          <button onClick={fetchAccount} className="p-2 rounded-md bg-surface border border-border text-text-secondary hover:text-text-primary hover:bg-surface-highlight transition-all" data-testid="refresh-account">
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <StatusCard
            account={account}
            teamUsers={teamUsers}
            completionState={getCompletionState()}
            isOwnTeam={isOwnTeam}
            canEditAccounts={canEditAccounts}
            hasEditAccess={hasEditAccess}
            canReassign={canReassign}
            onStatusChange={handleStatusChange}
            onAssigneeChange={handleAssigneeChange}
            onComplete={handleComplete}
          />
          <DevicesCard
            devices={account.devices}
            blockers={account.blockers}
            canAddDevices={canAddDevices}
            hasEditAccess={hasEditAccess}
            onAddDevice={() => setShowDeviceModal(true)}
          />
          <BlockersCard
            account={account}
            permissions={permissions}
            onBlockerAction={handleBlockerAction}
            onAddBlocker={() => setShowBlockerModal(true)}
          />
          <NotesCard notes={account.notes} onAddNote={() => setShowNoteModal(true)} />
        </div>
        <div className="space-y-6">
          <AccountInfoCard account={account} />
          <ActivityLog activityLog={account.activity_log} />
        </div>
      </div>

      {/* Modals */}
      {showDeviceModal && <DeviceModal onClose={() => setShowDeviceModal(false)} onSuccess={() => { setShowDeviceModal(false); fetchAccount(); }} accountId={accountId} />}
      {showBlockerModal && <BlockerModal onClose={() => setShowBlockerModal(false)} onSuccess={() => { setShowBlockerModal(false); fetchAccount(); }} accountId={accountId} devices={account.devices} />}
      {showNoteModal && <NoteModal onClose={() => setShowNoteModal(false)} onSuccess={() => { setShowNoteModal(false); fetchAccount(); }} accountId={accountId} />}
      {showEditAccountModal && <EditAccountModal account={account} onClose={() => setShowEditAccountModal(false)} onSuccess={() => { setShowEditAccountModal(false); fetchAccount(); }} />}
    </div>
  );
};

export default AccountDetail;
